import { FilterChip } from "../components/FilterChip";
import SearchableList from "../components/SearchableList";
import { FILTER_VALUES_LOADER_SIZE_PX } from "../types/constants";
import { groupOptionsByLabel } from "../utils/columnFilterUtils";
import { selectedValuesChipLabel } from "../utils/filterKindUtils";
import { Box, Button } from "@mui/material";
import DefaultLoader from "components/common/DefaultLoader";
import * as labels from "config.labels";
import React, { useMemo } from "react";
import * as staticDataHooks from "store/hooks/staticDataHooks";

const OptionFilter = ({
  columnDisplayName,
  searchQuery,
  onSearchQueryChange,
  uniqueValues,
  valueCounts,
  selectedValues,
  onToggleValue,
  isOpen,
  getValueDisplayLabel,
  getValueStartIcon,
  showActions = false,
  onSave,
  onCancel,
  emptyMessage,
  loading = false,
}) => {
  const translateLabels = staticDataHooks.useTranslator("uiLabels");
  const saveLabel = translateLabels(labels.OPTION_FILTER_SAVE, {
    default: "Save",
  });
  const cancelLabel = translateLabels(labels.OPTION_FILTER_CANCEL, {
    default: "Cancel",
  });
  // Group options by their displayed label so distinct internal values that
  // resolve to the same label (e.g. two brokers both in "Tel Aviv") collapse
  // into one row whose count is the sum and whose toggle covers every id.
  const groupedItems = useMemo(
    () =>
      groupOptionsByLabel(
        uniqueValues,
        valueCounts,
        getValueDisplayLabel,
        selectedValues,
        getValueStartIcon
      ),
    [
      uniqueValues,
      valueCounts,
      getValueDisplayLabel,
      selectedValues,
      getValueStartIcon,
    ]
  );
  return (
    <React.Fragment>
      {showActions && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 1,
            px: 1.5,
            pt: 1,
            pb: 1,
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <Button
            size={"small"}
            variant={"contained"}
            onClick={onSave}
            fullWidth
          >
            {saveLabel}
          </Button>
          <Button
            size={"small"}
            variant={"contained"}
            color={"secondary"}
            onClick={onCancel}
            fullWidth
          >
            {cancelLabel}
          </Button>
        </Box>
      )}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
          <DefaultLoader size={FILTER_VALUES_LOADER_SIZE_PX} />
        </Box>
      ) : (
        <SearchableList
          items={groupedItems}
          onItemClick={(item) => onToggleValue(item.ids)}
          isOpen={isOpen}
          searchPlaceholder={columnDisplayName}
          searchValue={searchQuery}
          onSearchChange={onSearchQueryChange}
          emptyMessage={
            emptyMessage ??
            translateLabels(labels.OPTION_FILTER_NO_VALUES, {
              default: "No values",
            })
          }
          sortAlphabetically={false}
        />
      )}
    </React.Fragment>
  );
};

export const renderOptionFilterChips = (
  selectedValues,
  onClear,
  getValueDisplayLabel
) => {
  if (selectedValues.size === 0) return null;
  const { label, tooltip } = selectedValuesChipLabel(
    selectedValues,
    getValueDisplayLabel
  );
  return (
    <FilterChip
      label={label}
      tooltip={tooltip}
      onDelete={onClear}
      sx={{
        ml: 1,
        mr: 1,
      }}
    />
  );
};

export default OptionFilter;
